import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
    SettingsCategory,
    SettingsVariable,
    createVariable,
} from '../../services/api/departmentVariablesService';
import { DepartmentUser, isLabelTaken } from './utils/settingsView';
import { useToast } from '../../hooks/use-toast';
import { RefVariablePicker } from './variable-row/RefVariablePicker';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

interface AddVariableRowProps {
    category: SettingsCategory;
    allVariables: SettingsVariable[];
    users: DepartmentUser[];
    onSaved: () => Promise<void>;
}

export default function AddVariableRow({
    category,
    allVariables,
    users,
    onSaved,
}: AddVariableRowProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [label, setLabel] = useState('');
    const [value, setValue] = useState('');
    const [refId, setRefId] = useState<string | undefined>();
    const [saving, setSaving] = useState(false);
    const isContact = category.tab === 'contacts';
    const canAdd = !saving && !!label.trim() && (!!refId || !!value.trim());

    const reset = () => {
        setLabel('');
        setValue('');
        setRefId(undefined);
        setIsOpen(false);
    };

    const add = async () => {
        if (!canAdd) return;
        if (isLabelTaken(allVariables, label, t)) {
            toast({
                title: t('departmentVariables.variableRow.labelExists'),
                variant: 'destructive',
            });
            return;
        }
        setSaving(true);
        try {
            await createVariable({
                type: isContact ? 'ref' : 'fixed',
                label: label.trim(),
                category: category.key,
                refId,
                fixedValue: refId ? undefined : value.trim(),
            });
            await onSaved();
            reset();
        } catch {
            return;
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) {
        return (
            <div className="px-4 py-3">
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setIsOpen(true)}
                >
                    <Plus className="h-4 w-4" />
                    {isContact
                        ? t('departmentVariables.category.addContact')
                        : t('departmentVariables.category.addValue')}
                </Button>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 items-start">
            <Input
                autoFocus
                value={label}
                placeholder={
                    isContact
                        ? t(
                              'departmentVariables.category.contactLabelPlaceholder'
                          )
                        : t(
                              'departmentVariables.category.valueLabelPlaceholder'
                          )
                }
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') reset();
                }}
            />
            <div className="space-y-2">
                {isContact ? (
                    <RefVariablePicker
                        options={users.map((user) => ({
                            id: user.id,
                            label: user.name,
                            description: user.role,
                        }))}
                        selectedId={refId}
                        freeTextValue={value}
                        canClear
                        placeholder={t(
                            'departmentVariables.variableRow.selectOrTypeName'
                        )}
                        searchPlaceholder={t(
                            'departmentVariables.variableRow.searchTeamOrType'
                        )}
                        emptyText={t(
                            'departmentVariables.variableRow.noTeamMembersFound'
                        )}
                        onSelect={(id) => {
                            setRefId(id);
                            setValue('');
                        }}
                        onSelectFreeText={(text) => {
                            setRefId(undefined);
                            setValue(text);
                        }}
                        onClear={() => {
                            setRefId(undefined);
                            setValue('');
                        }}
                    />
                ) : (
                    <Input
                        value={value}
                        maxLength={250}
                        placeholder={t(
                            'departmentVariables.category.valuePlaceholder'
                        )}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void add();
                            }
                            if (e.key === 'Escape') reset();
                        }}
                    />
                )}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={reset}>
                        {t('departmentVariables.category.cancel')}
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => void add()}
                        disabled={!canAdd}
                    >
                        {t('departmentVariables.category.add')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
